import axios from 'axios';

/**
 * Единственная граница с бэкендом.
 *
 * Панель зависит от имён полей API напрямую, без слоя преобразования, а
 * обращение к несуществующему полю в JS даёт не ошибку, а `undefined` в
 * интерфейсе. Именно так ссылка на несуществующее поле `wallet_address` в
 * таблице реестра годами вела на страницу адреса `undefined`, никем не
 * замеченная: текст в той же ячейке брался из правильного поля.
 *
 * Здесь форма ответа проверяется один раз при получении: переименование поля
 * на бэкенде превращается в громкую ошибку с именем поля вместо тихого пустого
 * места в таблице.
 */

const apiUrl = process.env.REACT_APP_API_URL;

const requireApiUrl = () => {
  if (!apiUrl) {
    // Без переменной адреса запросы уходили бы на `undefined/stakes` и падали
    // невнятной сетевой ошибкой.
    throw new Error(
      'Адрес API не задан. Соберите приложение с REACT_APP_API_URL.',
    );
  }

  return apiUrl;
};

const STAKE_REQUIRED_FIELDS = [
  'receiver',
  'amount',
  'staking_period',
  'rate',
  'unlock_time',
  'hash',
];

const missingFields = (stake) =>
  STAKE_REQUIRED_FIELDS.filter(
    (field) => stake?.[field] === undefined || stake?.[field] === null,
  );

/**
 * Реестр стейкингов.
 *
 * @returns {Promise<{stakes: Array, syncedBlock: string|null}>}
 */
export const fetchStakes = async (params) => {
  const response = await axios.get(`${requireApiUrl()}/stakes`, { params });

  if (!Array.isArray(response.data)) {
    throw new Error('Реестр вернул не список — формат ответа API изменился');
  }

  // Переименование поля затрагивает все записи сразу, поэтому достаточно
  // проверить первую: разбирать весь список ради того же вывода незачем.
  const [first] = response.data;

  if (first) {
    const missing = missingFields(first);

    if (missing.length > 0) {
      throw new Error(
        `В ответе реестра нет полей: ${missing.join(', ')}. ` +
          'Формат API изменился — нужна парная правка в staking-back.',
      );
    }
  }

  return {
    // До какого блока данные актуальны. Заголовка нет, пока историческая часть
    // не загружена на бэкенде.
    syncedBlock: response.headers['x-synced-block'] ?? null,
    stakes: response.data,
  };
};

/**
 * Бессрочные позиции.
 *
 * Обязательные поля проверяются так же, как у срочных: опечатка в имени даёт
 * не ошибку, а `undefined` в интерфейсе. Для тикающего счётчика это особенно
 * важно — пропавший `pendingRaw` превратил бы счётчик в `NaN`, а пропавший
 * `pendingAt` тихо заморозил бы его на снимке минутной давности.
 */
const PERPETUAL_STAKE_REQUIRED_FIELDS = [
  'address',
  'principal',
  'principalRaw',
  'pendingRaw',
  'totalClaimed',
  'aprRaw',
  'startedAt',
];

const PERPETUAL_DEPOSIT_REQUIRED_FIELDS = [
  'txHash',
  'address',
  'amount',
  'principalAfter',
  'sender',
  'depositedAt',
];

const missingIn = (item, fields) =>
  fields.filter(
    (field) => item?.[field] === undefined || item?.[field] === null,
  );

/** Проверяет форму первой записи списка: переименование поля задевает все. */
const assertListShape = (data, fields, what) => {
  if (!Array.isArray(data)) {
    throw new Error(`${what}: ответ не список — формат API изменился`);
  }

  const [first] = data;

  if (first) {
    const missing = missingIn(first, fields);

    if (missing.length > 0) {
      throw new Error(
        `${what}: в ответе нет полей: ${missing.join(', ')}. ` +
          'Формат API изменился — нужна парная правка в staking-back.',
      );
    }
  }

  return data;
};

/** Позиции бессрочного стейкинга. @returns {Promise<Array>} */
export const fetchPerpetualStakes = async (params) => {
  const response = await axios.get(`${requireApiUrl()}/perpetual/stakes`, {
    params,
  });

  return assertListShape(
    response.data,
    PERPETUAL_STAKE_REQUIRED_FIELDS,
    'Реестр бессрочных',
  );
};

/**
 * Текущая ставка, минимум взноса и история изменений.
 *
 * `apr` и `minStake` бэкенд читает из контракта, историю — из своей базы.
 * История может быть пустой: сервис видит её только с момента запуска.
 */
export const fetchPerpetualRate = async () => {
  const response = await axios.get(`${requireApiUrl()}/perpetual/rate`);
  const { apr, aprRaw, minStake, history } = response.data ?? {};

  if (typeof apr !== 'number' || typeof aprRaw !== 'number') {
    throw new Error('В ответе о ставке нет числовых полей apr и aprRaw');
  }

  return {
    apr,
    aprRaw,
    minStake,
    history: Array.isArray(history) ? history : [],
  };
};

/** История взносов; без фильтра — по всем адресам. @returns {Promise<Array>} */
export const fetchPerpetualDeposits = async (params) => {
  const response = await axios.get(`${requireApiUrl()}/perpetual/deposits`, {
    params,
  });

  return assertListShape(
    response.data,
    PERPETUAL_DEPOSIT_REQUIRED_FIELDS,
    'История взносов',
  );
};

/**
 * Правка расписания автоклейма — единственный запрос панели, который пишет.
 *
 * Меняет настройку бэкенда, а не данные блокчейна, но требует подписи кошелька:
 * по ней бэкенд восстанавливает адрес и спрашивает у контракта
 * `hasRole(ADMIN_ROLE)`. Подпись собирается в `utils/signSchedule.js` и
 * приезжает сюда готовой — эта функция про HTTP, а не про кошелёк.
 *
 * Окна подтверждения перед этим запросом нет: кошелёк и так показывает
 * подписываемый текст целиком, а изменение обратимо и не стоит газа.
 */
export const updatePerpetualSchedule = async (address, changes, auth) => {
  const response = await axios.put(
    `${requireApiUrl()}/perpetual/schedule/${address}`,
    changes,
    {
      headers: {
        'X-Signature': auth.signature,
        'X-Signed-At': auth.issuedAt,
      },
    },
  );

  const { periodDays, nextClaimAt, enabled } = response.data ?? {};

  if (typeof periodDays !== 'number' || typeof enabled !== 'boolean') {
    throw new Error('В ответе о расписании нет полей periodDays и enabled');
  }

  return { periodDays, nextClaimAt, enabled };
};

/** Баланс токенов на адресе. @returns {Promise<number>} */
export const fetchTokenBalance = async (address) => {
  const response = await axios.get(
    `${requireApiUrl()}/balances/token/${address}`,
  );

  const balance = Number(response.data?.balance);

  if (!Number.isFinite(balance)) {
    throw new Error('В ответе о балансе токенов нет поля balance');
  }

  return balance;
};

/** Баланс нативной монеты в долларах. @returns {Promise<number>} */
export const fetchNativeUsdBalance = async (address) => {
  const response = await axios.get(
    `${requireApiUrl()}/balances/native/${address}`,
  );

  const usd = response.data?.usd;

  if (typeof usd !== 'number') {
    throw new Error('В ответе о балансе кошелька нет числового поля usd');
  }

  return usd;
};

/** Причина отказа человеческим языком: у axios она спрятана в ответе. */
export const apiErrorMessage = (error) =>
  error?.response?.data?.error ??
  error?.response?.data?.details ??
  error?.message ??
  'причина неизвестна';
